export interface YouTubeUrlInfo {
  isValid: boolean;
  videoId: string | null;
  originalUrl: string;
  normalizedUrl: string | null;
}

const YOUTUBE_URL_REGEX = /^(?:https?:\/\/)?(?:www\.|m\.)?(?:youtube\.com\/(?:watch\?v=|embed\/|v\/|.+\?v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})(?:\S+)?$/;

const YOUTUBE_URL_REGEX_EXTENDED = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;

/**
 * Validates if a URL is a valid YouTube URL
 * @param url - The URL to validate
 * @returns boolean indicating if the URL is a valid YouTube URL
 */
export const isYouTubeUrl = (url: string): boolean => {
  if (!url || typeof url !== 'string') {
    return false;
  }

  const trimmedUrl = url.trim();

  if (YOUTUBE_URL_REGEX.test(trimmedUrl)) {
    return true;
  }

  const match = trimmedUrl.match(YOUTUBE_URL_REGEX_EXTENDED);
  if (match && match[7] && match[7].length === 11) {
    return true;
  }

  return false;
};

/**
 * Extracts video ID from a YouTube URL
 * @param url - The YouTube URL
 * @returns The video ID or null if not found
 */
export const extractYouTubeVideoId = (url: string): string | null => {
  if (!url || typeof url !== 'string') {
    return null;
  }

  const trimmedUrl = url.trim();

  const primaryMatch = trimmedUrl.match(YOUTUBE_URL_REGEX);
  if (primaryMatch && primaryMatch[1]) {
    return primaryMatch[1];
  }

  const extendedMatch = trimmedUrl.match(YOUTUBE_URL_REGEX_EXTENDED);
  if (extendedMatch && extendedMatch[7] && extendedMatch[7].length === 11) {
    return extendedMatch[7];
  }

  return null;
};

/**
 * Validates and parses a YouTube URL, returning comprehensive information
 * @param url - The URL to validate and parse
 * @returns YouTubeUrlInfo object with validation and parsing results
 */
export const validateYouTubeUrl = (url: string): YouTubeUrlInfo => {
  const result: YouTubeUrlInfo = {
    isValid: false,
    videoId: null,
    originalUrl: url,
    normalizedUrl: null,
  };

  if (!url || typeof url !== 'string') {
    return result;
  }

  const trimmedUrl = url.trim();
  const videoId = extractYouTubeVideoId(trimmedUrl);

  if (videoId) {
    result.isValid = true;
    result.videoId = videoId;
    result.normalizedUrl = `https://www.youtube.com/watch?v=${videoId}`;
  }

  return result;
};

/**
 * Generates a YouTube thumbnail URL from a video ID
 * @param videoId - The YouTube video ID
 * @param quality - Thumbnail quality ('default', 'medium', 'high', 'standard', 'maxres')
 * @returns The thumbnail URL
 */
export const getYouTubeThumbnailUrl = (
  videoId: string,
  quality: 'default' | 'medium' | 'high' | 'standard' | 'maxres' = 'medium',
): string => {
  if (!videoId) {
    return '';
  }

  const qualityMap = {
    default: 'default',
    medium: 'mqdefault',
    high: 'hqdefault',
    standard: 'sddefault',
    maxres: 'maxresdefault',
  };

  const qualityString = qualityMap[quality] || 'mqdefault';
  return `https://img.youtube.com/vi/${videoId}/${qualityString}.jpg`;
};

/**
 * Generates an embeddable YouTube URL from a video ID
 * @param videoId - The YouTube video ID
 * @param autoplay - Whether to enable autoplay
 * @param startTime - Start time in seconds
 * @returns The embeddable YouTube URL
 */
export const getYouTubeEmbedUrl = (
  videoId: string,
  autoplay: boolean = false,
  startTime?: number,
): string => {
  if (!videoId) {
    return '';
  }

  let embedUrl = `https://www.youtube.com/embed/${videoId}`;
  const params: string[] = [];

  if (autoplay) {
    params.push('autoplay=1');
  }

  if (startTime && startTime > 0) {
    params.push(`start=${startTime}`);
  }

  if (params.length > 0) {
    embedUrl += `?${params.join('&')}`;
  }

  return embedUrl;
};

/**
 * Checks if a YouTube video is accessible (basic check)
 * Note: This is a simple check and may not catch all cases
 * @param videoId - The YouTube video ID
 * @returns Promise<boolean> indicating if the video is accessible
 */
export const checkYouTubeVideoAccessibility = async (videoId: string): Promise<boolean> => {
  if (!videoId) {
    return false;
  }

  try {
    const thumbnailUrl = getYouTubeThumbnailUrl(videoId, 'default');
    const response = await fetch(thumbnailUrl, { method: 'HEAD' });
    return response.ok;
  } catch {
    return false;
  }
};
